import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { Outlet } from "react-router-dom";
import SideBar from "../components/SideBar";
import { Button, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../store/store";
import { useTranslation } from "react-i18next";

const drawerWidth = 240;

interface AppLayoutProps {
  handleLogout: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

export default function AppLayout({ handleLogout }: AppLayoutProps) {
  const isLoggedIn = useSelector((state: RootState) => state.app.isLoggedIn);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const [viewTerms, setViewTerms] = React.useState<"T" | "P" | "L" | "">("");
  const { t: tCommon } = useTranslation("common");

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    // alert("handleDrawerToggle");
    // if (!isClosing) {
    //   setMobileOpen(!mobileOpen);
    // }
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        display: "flex",
        bgcolor: (theme) => theme.palette.grey[100],
        minHeight: "100vh",
      }}
    >
      <CssBaseline />

      {/* トップバー */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
        }}
      >
        <Toolbar sx={{ backgroundColor: "#69c779" }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{ fontWeight: "fontWeightBold", color: "#f1f1a1" }}
          >
            {/* 収支履歴 | 家計簿 */}
            {tCommon("app.title")}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* サイドバー */}
      <SideBar
        drawerWidth={drawerWidth}
        mobileOpen={mobileOpen}
        handleDrawerTransitionEnd={handleDrawerTransitionEnd}
        handleDrawerClose={handleDrawerClose}
        isLoggedIn={isLoggedIn}
        handleLogout={handleLogout}
      />

      {/* MAINコンテンツ */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: { xs: 1, sm: 3 },
          py: 3,
          width: { xs: "100%", sm: `calc(100% - ${drawerWidth}px)` },
          minWidth: 0,
          boxSizing: "border-box",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Toolbar />
        <Box sx={{ flexGrow: 1 }}>
          <Outlet />
        </Box>
        <Box
          component="footer"
          sx={{
            mt: 1.5,
            py: 1,
            width: { xs: "100%", lg: `calc(100% - 320px)` },
            boxSizing: "border-box",
            textAlign: "center",
            color: "text.secondary",
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <Typography variant="body2">© 2026 Copyright TwoNine.</Typography>
          <Stack
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              gap: "8px",
            }}
          >
            <Button
              sx={{ textDecoration: "none", color: "text.secondary" }}
              variant="text"
              onClick={() => setViewTerms("T")}
            >
              {tCommon("terms.termsOfUse")}
            </Button>
            <Button
              sx={{ textDecoration: "none", color: "text.secondary" }}
              variant="text"
              onClick={() => setViewTerms("P")}
            >
              {tCommon("terms.privacyPolicy")}
            </Button>
            <Button
              sx={{ textDecoration: "none", color: "text.secondary" }}
              variant="text"
              onClick={() => setViewTerms("L")}
            >
              {tCommon("terms.limitationLiability")}
            </Button>
          </Stack>
          <Typography
            component="div"
            sx={{
              whiteSpace: "pre-line", // 줄바꿈(엔터)을 그대로 반영
              lineHeight: 1.8, // 가독성을 위한 줄간격
              color: "text.primary",
              background: "white",
            }}
            onClick={() => setViewTerms("")}
          >
            {viewTerms === "T"
              ? // ? termText
                tCommon("terms.textOfTu")
              : viewTerms === "P"
                ? tCommon("terms.textOfPp")
                : viewTerms == "L"
                  ? tCommon("terms.textOfLol")
                  : null}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
